import MarketTabSection from '../../../common/MarketTabSection';
import { useOrderDetails } from '../../OrderDetailsProvider';
import { fetchMembershipOrderDetailsPreviewOrderList } from '../../membership-order-details-mock';

import { CPLAT_API_ROUTES } from '@/api';
import { useMembershipPreviewMode } from '@/components/membership/MembershipContentLock';
import GlobalSpinner from '@/components/organisms/GlobalSpinner';
import { useAppSelector } from '@/redux/store';
import { useState } from 'react';

import { fetchOrderChangeList, orderQueries, useFetch } from '@/api';
import { DEFAULT_PAGE_SIZE } from '@/constants/order';
import type { OrderSeachField } from '@/types/order-list';
import { LayoutStickySection, LayoutTables } from '@/pages/orders/_layouts/LayoutOrderContainer';
import LayoutTableController from '@/pages/orders/_layouts/LayoutTableController';
import type { CplatApiResponse } from '@/types/api';
import type { Stores } from '@/types/stores';
import { formatISOString } from '@/utils/format-iso-string';
import DateRangeSelect from '../../../common/DateRangeSelect';
import NotRegisteredState from '../../../common/NotRegisteredState';
import { SearchFilter } from '../../../common/SearchFilter';
import OrderTableColumn from './OrderTableColumn';
import { useGetMarketIdFromSeachParams } from '@/hooks/use-get-market-id-from-search-params';
import { useInfiniteOrderList } from '@/pages/orders/_hooks/useInfiniteOrderList';
import { useSort } from '@/contexts/table-sort';

const Return = () => {
  const user = useAppSelector((state) => state.user);
  const isPreviewMode = useMembershipPreviewMode();

  const [searchField, setSearchField] = useState<OrderSeachField>('ExternalProductInfo');
  const [searchKeyword, setSearchKeyword] = useState('');

  const {
    // 날짜 관련 상태와 함수
    startDate,
    endDate,
    handleStartDate,
    handleEndDate,
  } = useOrderDetails();
  const { storeId, marketId } = useGetMarketIdFromSeachParams();
  const { sort } = useSort();

  const filterParams = {
    dateFrom: formatISOString(startDate),
    dateTo: formatISOString(endDate),
    ...(storeId && { storeId }),
    ...(marketId && { marketId: Number(marketId) }),
    ...(sort && {
      sortInfo: { sortColumn: sort.sortColumn, sortDirection: sort.sortDirection },
    }),
    ...(searchField &&
      searchKeyword && {
        searchField,
        searchKeyword,
      }),
  };

  const getOrderChangeListQuery = useInfiniteOrderList({
    queryKey: [
      ...orderQueries.all(),
      'getOrderChangeList',
      'infinite',
      'ReturnRequest',
      filterParams,
    ],
    pageSize: DEFAULT_PAGE_SIZE,
    enabled: isPreviewMode || Boolean(user.CplatToken),
    queryFn: ({ skip, take }) =>
      isPreviewMode
        ? fetchMembershipOrderDetailsPreviewOrderList('ReturnRequest', {
            ...filterParams,
            skip,
            take,
          })
        : fetchOrderChangeList({
            cplatToken: user.CplatToken,
            ...filterParams,
            skip,
            take,
            statusTab: 'ReturnRequest',
          }),
  });

  const orderChangeListData = getOrderChangeListQuery.orderList;

  const { data: storeListData } = useFetch<CplatApiResponse<Stores[][]>>(
    CPLAT_API_ROUTES.getStores,
    {
      method: 'POST',
      params: { cplatToken: user.CplatToken },
      config: { enabled: !isPreviewMode },
      v3: true,
    },
  );

  const hasRegisteredStores = isPreviewMode || (storeListData?.data[0].length ?? 0) >= 1;

  if (getOrderChangeListQuery.isLoading) return <GlobalSpinner />;

  return (
    <>
      {hasRegisteredStores ? (
        <>
          <LayoutStickySection>
            <MarketTabSection
              totalLength={
                getOrderChangeListQuery.pagination?.totalCount ?? orderChangeListData.length
              }
            />
            <LayoutTableController>
              <div className="flex gap-[20px]">
                <DateRangeSelect
                  startDate={startDate}
                  handleStartDate={handleStartDate}
                  endDate={endDate}
                  handleEndDate={handleEndDate}
                />
                <SearchFilter
                  searchField={searchField}
                  searchKeyword={searchKeyword}
                  setSearchKeyword={setSearchKeyword}
                  setSearchField={setSearchField}
                  onSearch={(keyword) => setSearchKeyword(keyword)}
                  onReset={() => setSearchKeyword('')}
                />
              </div>
            </LayoutTableController>
          </LayoutStickySection>
          <LayoutTables isTableBorder>
            <OrderTableColumn
              orderList={orderChangeListData}
              listEndSentinelRef={getOrderChangeListQuery.listEndSentinelRef}
              hasNextPage={getOrderChangeListQuery.hasNextPage}
              isFetchingNextPage={getOrderChangeListQuery.isFetchingNextPage}
            />
          </LayoutTables>
        </>
      ) : (
        <NotRegisteredState className="bg-white" />
      )}
    </>
  );
};

export default Return;
